import { campaignSlug } from "src/services/URLHandler";

type CampaignCandidate = {
    id: number;
    active?: boolean;
};

type CampaignPath = {
    hasCampaignSegment: boolean;
    campaignId: number | null;
};

type CampaignResolution<T> = {
    campaign: T | null;
    shouldReplacePath: boolean;
};

export const parseCampaignPath = (pathname: string): CampaignPath => {
    const segments = pathname.split("/");
    const campaignIndex = segments.findIndex((segment) => segment === campaignSlug);

    if (campaignIndex === -1) {
        return { hasCampaignSegment: false, campaignId: null };
    }

    const rawCampaignId = segments[campaignIndex + 1];
    if (rawCampaignId === undefined || !/^\d+$/.test(rawCampaignId)) {
        return { hasCampaignSegment: true, campaignId: null };
    }

    const campaignId = Number(rawCampaignId);

    return {
        hasCampaignSegment: true,
        campaignId: Number.isSafeInteger(campaignId) && campaignId > 0 ? campaignId : null,
    };
};

export const replaceCampaignIdInPath = (pathname: string, campaignId: number): string => {
    const segments = pathname.split("/");
    const campaignIndex = segments.findIndex((segment) => segment === campaignSlug);

    if (campaignIndex === -1) {
        return pathname;
    }

    segments[campaignIndex + 1] = campaignId.toString();

    return segments.join("/");
};

export const resolveCampaignFromPath = <T extends CampaignCandidate>(
    campaigns: T[],
    pathname: string,
    storedCampaignId: number | null,
): CampaignResolution<T> => {
    const { hasCampaignSegment, campaignId } = parseCampaignPath(pathname);
    const campaignFromPath = campaigns.find((campaign) => campaign.id === campaignId);

    if (campaignFromPath !== undefined) {
        return { campaign: campaignFromPath, shouldReplacePath: false };
    }

    const storedCampaign = campaigns.find((campaign) => campaign.id === storedCampaignId);
    const campaign = storedCampaign ?? campaigns.find((candidate) => candidate.active) ?? campaigns.at(-1) ?? null;

    return {
        campaign,
        shouldReplacePath: hasCampaignSegment && campaign !== null,
    };
};
